/**
 * Card Component
 *
 * A versatile card container with multiple variants for different UI contexts.
 * Supports hover effects, borders, and glass morphism styling.
 */

import type { ReactNode } from 'react';

export interface CardProps {
  children: ReactNode;
  variant?: 'default' | 'elevated' | 'bordered' | 'glass' | 'interactive';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  rounded?: 'md' | 'lg' | 'xl' | '2xl' | '3xl';
  className?: string;
  onClick?: () => void;
  as?: 'div' | 'article' | 'section';
}

export default function Card({
  children,
  variant = 'default',
  padding = 'md',
  rounded = '2xl',
  className = '',
  onClick,
  as: Component = 'div',
}: CardProps) {
  const baseClasses = 'transition-all duration-200';

  const variantClasses: Record<Required<CardProps>['variant'], string> = {
    default: 'bg-white dark:bg-[var(--pv-color-surface-strong)]',
    elevated: 'bg-white dark:bg-[var(--pv-color-surface-strong)] shadow-lg hover:shadow-xl',
    bordered: 'bg-white dark:bg-[var(--pv-color-surface-strong)] border border-[var(--pv-border-soft)]',
    glass: 'bg-white dark:bg-[var(--pv-color-surface-strong)] shadow-brand-sm border border-[var(--pv-border-subtle)]',
    interactive: 'bg-white dark:bg-[var(--pv-color-surface-strong)] border border-[var(--pv-border-soft)] hover:border-[var(--pv-color-primary)] hover:shadow-lg cursor-pointer',
  };

  const paddingClasses: Record<Required<CardProps>['padding'], string> = {
    none: '',
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-6',
  };

  const roundedClasses: Record<Required<CardProps>['rounded'], string> = {
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    '2xl': 'rounded-2xl',
    '3xl': 'rounded-3xl',
  };

  const cardClasses = `${baseClasses} ${variantClasses[variant]} ${paddingClasses[padding]} ${roundedClasses[rounded]} ${className}`;

  return (
    <Component
      className={cardClasses}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {children}
    </Component>
  );
}

// Card sub-components for structured layouts
export function CardHeader({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`border-b border-[var(--pv-border-soft)] pb-4 mb-4 ${className}`}>
      {children}
    </div>
  );
}

export function CardTitle({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <h3 className={`font-semibold text-lg text-[var(--pv-color-text)] ${className}`}>
      {children}
    </h3>
  );
}

export function CardDescription({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <p className={`text-sm text-[var(--pv-color-text-muted)] mt-1 ${className}`}>
      {children}
    </p>
  );
}

export function CardContent({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={className}>{children}</div>;
}

export function CardFooter({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`border-t border-[var(--pv-border-soft)] pt-4 mt-4 ${className}`}>
      {children}
    </div>
  );
}
